// BottomNav — manager tab bar.
// Faithful to the design bundle's .botnav markup (ui.jsx · BottomNav), wired to
// react-router and a live overdue badge.
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'

import { apiRequest } from '../api/client'
import { useAiFeatures } from '../api/features'
import type { OverdueResponse } from '../api/types'
import { getWebApp } from '../telegram/tg'
import { Icon, t } from '../design'

type TabId = 'today' | 'deals' | 'overdue' | 'ai' | 'profile'

const TABS: { id: TabId; icon: JSX.Element; labelKey: string; path: string }[] = [
  { id: 'today', icon: <Icon.Today size={22} />, labelKey: 'nav.today', path: '/today' },
  { id: 'deals', icon: <Icon.Briefcase size={22} />, labelKey: 'nav.deals', path: '/deals' },
  { id: 'overdue', icon: <Icon.AlertTri size={22} />, labelKey: 'nav.overdue', path: '/overdue' },
  { id: 'ai', icon: <Icon.Sparkle size={22} />, labelKey: 'nav.ai', path: '/ai' },
  { id: 'profile', icon: <Icon.User size={22} />, labelKey: 'nav.profile', path: '/profile' },
]

export function BottomNav() {
  const location = useLocation()
  const navigate = useNavigate()
  const features = useAiFeatures()
  // Director can hide the whole «AI» tab for the team.
  const tabs = features.ai_tab ? TABS : TABS.filter((tb) => tb.id !== 'ai')

  // Light-touch badge: count of overdue items. Reuses the same query
  // key OverdueScreen uses so navigating there gets a warm cache.
  const { data: overdue } = useQuery({
    queryKey: ['overdue'],
    queryFn: () => apiRequest<OverdueResponse>('/api/miniapp/overdue'),
    staleTime: 60_000,
  })
  const overdueCount = overdue?.summary?.total ?? 0

  return (
    <nav className="botnav" role="tablist" aria-label="Основная навигация">
      {tabs.map((tab) => {
        const active = matchTab(location.pathname, tab.id)
        const badge = tab.id === 'overdue' && overdueCount > 0 ? overdueCount : null
        return (
          <button
            key={tab.id}
            className={`botnav-item ${active ? 'is-active' : ''}`}
            onClick={() => {
              try { getWebApp()?.HapticFeedback?.impactOccurred('light') } catch { /* noop */ }
              navigate(tab.path)
            }}
            role="tab"
            aria-selected={active}
          >
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              {tab.icon}
              {badge != null && (
                <span style={{
                  position: 'absolute', top: -4, right: -7, minWidth: 16, height: 16, padding: '0 4px',
                  borderRadius: 100, background: 'var(--danger)', color: '#fff',
                  font: '700 10px/16px var(--font-sans)', textAlign: 'center',
                  border: '2px solid var(--surface)', boxSizing: 'content-box',
                }}>{badge}</span>
              )}
            </span>
            <span>{t(tab.labelKey)}</span>
          </button>
        )
      })}
    </nav>
  )
}

/** True if the current pathname belongs to this tab's area. */
function matchTab(pathname: string, tab: TabId): boolean {
  if (tab === 'today') return pathname === '/' || pathname.startsWith('/today')
  if (tab === 'deals') return pathname.startsWith('/deals')
  if (tab === 'overdue') return pathname.startsWith('/overdue')
  if (tab === 'ai') return pathname.startsWith('/ai')
  if (tab === 'profile') return pathname.startsWith('/profile')
  return false
}
